import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { partesVbApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, RefreshCw, UtensilsCrossed, Clock, Lock, PenLine,
  CheckCircle, CheckCircle2, AlertCircle, Users, Calendar, ShieldCheck,
  Printer, FileSpreadsheet, Stamp,
} from 'lucide-react';
import { exportToCSV, printTable } from '../../lib/printUtils';

const TERCER_DIA_MS = 3 * 24 * 60 * 60 * 1000;

function formatDateShort(iso) {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('es-CL', { weekday: 'short', day: '2-digit', month: '2-digit' });
  } catch { return iso; }
}

function LunchChip({ parte }) {
  if (parte.almuerzo_enviado) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-200">
        <CheckCircle className="h-3 w-3" /> Solicitado ({parte.total_almuerzos})
      </span>
    );
  }
  if (parte.total_almuerzos > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-900/30 dark:text-amber-200">
        <AlertCircle className="h-3 w-3" /> Pendiente enviar ({parte.total_almuerzos})
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-300 dark:bg-gray-800 dark:text-gray-400">
      Sin almuerzos
    </span>
  );
}

function VBStatusChip({ parte }) {
  // Solo aplica a partes cerrados; los borradores no requieren VB todavía.
  if (parte.estado !== 'cerrado') {
    return <span className="text-[10px] text-muted-foreground">—</span>;
  }
  if (!parte.supervisor_nombre || !parte.supervisor_nombre.trim()) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-500 border border-gray-300"
            title="Sin supervisor asignado">
        S/Sup.
      </span>
    );
  }
  const vb = parte.vb_supervisor;
  if (vb && vb.dado) {
    const tip = `VB de ${vb.nombre || vb.email} el ${vb.fecha ? new Date(vb.fecha).toLocaleString('es-CL') : '?'}${vb.comentario ? ` · "${vb.comentario}"` : ''}`;
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-200" title={tip}>
        <CheckCircle2 className="h-3 w-3" /> VB
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-900/30 dark:text-amber-200"
          title="Cerrado pero sin VB del supervisor">
      <Clock className="h-3 w-3" /> Pend.
    </span>
  );
}

function AsignacionChip({ parte }) {
  if (parte.estado === 'cerrado') {
    if (parte.hh_mismatch) {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-800 border border-red-400 dark:bg-red-900/30 dark:text-red-200"
              title={`Inconsistencia: el parte declara ${parte.hh_generados} HH pero hay ${parte.hh_reales} registros en la base. Posible duplicación o pérdida de HH.`}
              data-testid={`hh-mismatch-${parte.parte_id}`}>
          <AlertCircle className="h-3 w-3" /> HH: {parte.hh_reales}/{parte.hh_generados} ⚠
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-200">
        <Lock className="h-3 w-3" /> Cerrado · {parte.hh_generados} HH
      </span>
    );
  }
  // Borrador: si tiene presentes completos, sugerir que está listo para cerrar
  const todosCompletos = parte.total_presentes > 0 && parte.total_presentes === (parte.total_personal - parte.total_ausentes);
  if (todosCompletos && parte.total_presentes > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-900/30 dark:text-amber-200"
            title="Horarios completos — falta cerrar el parte para generar HH">
        <AlertCircle className="h-3 w-3" /> Listo para cerrar · {parte.total_presentes}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800 border border-blue-300 dark:bg-blue-900/30 dark:text-blue-200">
      <PenLine className="h-3 w-3" /> Borrador · {parte.total_presentes}/{parte.total_personal}
    </span>
  );
}

export default function MonitorPartesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ['admin', 'administrador'].includes((user?.rol || '').toLowerCase());
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ days: [], partes: [] });
  const today = new Date().toISOString().slice(0, 10);
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [fechaDesde, setFechaDesde] = useState(sevenDaysAgo);
  const [fechaHasta, setFechaHasta] = useState(today);
  const [diaSeleccionado, setDiaSeleccionado] = useState(today);

  // Iter 236.q (Carlos): VB masivo mes (admin-only, acción extraordinaria)
  const [vbMasivoOpen, setVbMasivoOpen] = useState(false);
  const [vbMasivoLoading, setVbMasivoLoading] = useState(false);
  const aplicarVbMasivo = async () => {
    setVbMasivoLoading(true);
    try {
      const mesActual = new Date().toISOString().slice(0, 7); // YYYY-MM
      const res = await api.post('/partes-vb/aplicar-vb-mes', null, { params: { mes: mesActual } });
      toast.success(`V°B° aplicado a ${res.data?.actualizados ?? 0} parte(s) del mes ${res.data?.mes}`);
      setVbMasivoOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al aplicar V°B° masivo');
    } finally {
      setVbMasivoLoading(false);
    }
  };


  // Modo kiosko: cualquier usuario logueado puede abrir este modal y firmar
  // con el PIN del supervisor asignado al parte.
  const [vbParte, setVbParte] = useState(null);
  const [vbPin, setVbPin] = useState('');
  const [vbComentario, setVbComentario] = useState('');
  const [vbSubmitting, setVbSubmitting] = useState(false);

  const openDarVB = (parte) => {
    setVbParte(parte);
    setVbPin('');
    setVbComentario('');
  };

  const submitDarVB = async () => {
    if (!vbPin || vbPin.length < 4) {
      toast.error('PIN inválido (mínimo 4 dígitos)');
      return;
    }
    setVbSubmitting(true);
    try {
      await partesVbApi.darVB(vbParte.parte_id, vbPin, vbComentario);
      toast.success(`VB registrado por ${vbParte.supervisor_nombre}`);
      setVbParte(null);
      setVbPin('');
      setVbComentario('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al registrar VB');
    } finally {
      setVbSubmitting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/rrhh/partes-monitor', {
        params: { fecha_desde: fechaDesde, fecha_hasta: fechaHasta },
      });
      setData(r.data);
    } catch {
      setData({ days: [], partes: [] });
    } finally {
      setLoading(false);
    }
  }, [fechaDesde, fechaHasta]);

  useEffect(() => { load(); }, [load]);

  const partesDelDia = data.partes.filter(p => p.fecha_iso === diaSeleccionado);
  const dayStats = data.days.find(d => d.fecha_iso === diaSeleccionado);

  // Partes "pendientes" en el rango: abiertos sin cerrar
  const partesPendientes = data.partes.filter(p => p.estado !== 'cerrado');
  const partesSinAlmuerzo = data.partes.filter(p => !p.almuerzo_enviado && p.total_almuerzos > 0);

  const irAlParte = (parte_id) => {
    navigate(`/rrhh/parte-diario?open=${encodeURIComponent(parte_id)}`);
  };

  // Carlos (10/05/2026): imprimir y exportar Monitor de Partes
  const imprimirMonitor = () => {
    if (!data.partes.length) { toast.warning('Nada para imprimir'); return; }
    printTable({
      title: 'Monitor Diario de Partes',
      subtitle: `Rango: ${fechaDesde} → ${fechaHasta} · ${data.partes.length} partes · ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Total partes', value: data.partes.length },
        { label: 'Pendientes (no cerrados)', value: partesPendientes.length, color: '#b45309' },
        { label: 'Sin almuerzo enviado', value: partesSinAlmuerzo.length, color: '#b91c1c' },
        { label: 'Cerrados', value: data.partes.filter(p => p.estado === 'cerrado').length, color: '#047857' },
      ],
      columns: [
        { key: 'fecha_iso', label: 'Fecha', format: (v) => formatDateShort(v) },
        { key: 'trabajador_nombre', label: 'Trabajador' },
        { key: 'ot_numero', label: 'OT', align: 'center' },
        { key: 'horario', label: 'Horario', align: 'center' },
        { key: 'estado', label: 'Estado', align: 'center', format: (v) => (v || '').toUpperCase() },
        { key: 'total_almuerzos', label: 'Almuerzos', align: 'center' },
        { key: 'almuerzo_enviado', label: 'Almuerzo Env.', align: 'center', format: (v) => v ? 'SÍ' : 'NO' },
        { key: 'estado_vb', label: 'VB', align: 'center', format: (v) => (v || 'pend').toUpperCase() },
      ],
      rows: data.partes,
    });
  };

  const exportarMonitorExcel = () => {
    if (!data.partes.length) { toast.warning('Nada para exportar'); return; }
    exportToCSV({
      filename: `monitor_partes_${fechaDesde}_${fechaHasta}`,
      title: 'CHIRINO STEEL · Monitor Diario de Partes',
      summary: [
        { label: 'Rango desde', value: fechaDesde },
        { label: 'Rango hasta', value: fechaHasta },
        { label: 'Total partes', value: data.partes.length },
        { label: 'Pendientes', value: partesPendientes.length },
        { label: 'Sin almuerzo enviado', value: partesSinAlmuerzo.length },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'fecha_iso', label: 'Fecha' },
        { key: 'trabajador_nombre', label: 'Trabajador' },
        { key: 'rut', label: 'RUT' },
        { key: 'ot_numero', label: 'OT' },
        { key: 'horario', label: 'Horario' },
        { key: 'estado', label: 'Estado' },
        { key: 'total_almuerzos', label: 'Almuerzos solicitados' },
        { key: 'almuerzo_enviado', label: 'Almuerzo enviado', format: (v) => v ? 'SÍ' : 'NO' },
        { key: 'estado_vb', label: 'Estado VB' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows: data.partes,
    });
  };

  return (
    <div className="space-y-4 page-enter" data-testid="monitor-partes-page">
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="rounded-xl gap-1.5 text-xs mt-1" data-testid="btn-volver-monitor">
          <ArrowLeft className="h-4 w-4" /> Volver
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Calendar className="h-6 w-6 text-primary" /> Monitor Diario de Partes
          </h1>
          <p className="text-sm text-muted-foreground">Vista rápida del estado de almuerzos y asignación de OT/Horario por día.</p>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="rounded-xl gap-1.5" data-testid="btn-refresh">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Actualizar
          </Button>
          <Button variant="outline" size="sm" onClick={imprimirMonitor} className="rounded-xl gap-1.5" data-testid="btn-print-monitor-partes" title="Imprimir monitor">
            <Printer className="h-3.5 w-3.5" /> Imprimir
          </Button>
          <Button variant="outline" size="sm" onClick={exportarMonitorExcel} className="rounded-xl gap-1.5 text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="btn-excel-monitor-partes" title="Exportar a Excel">
            <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
          </Button>
          {/* Iter 236.q (Carlos): VB masivo del mes — solo admin */}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setVbMasivoOpen(true)}
              className="rounded-xl gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
              data-testid="btn-vb-masivo-mes"
              title="Aplicar V°B° a todos los partes del mes (acción extraordinaria)"
            >
              <Stamp className="h-3.5 w-3.5" /> V°B° mes
            </Button>
          )}
        </div>
      </div>

      {/* Filtros de rango */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Desde</span>
            <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="h-9 w-40" data-testid="input-fecha-desde" />
            <span className="text-muted-foreground">Hasta</span>
            <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="h-9 w-40" data-testid="input-fecha-hasta" />
          </div>
          <div className="flex-1" />
          <div className="text-xs text-muted-foreground">
            Total partes en rango: <strong>{data.total_partes || 0}</strong>
          </div>
        </CardContent>
      </Card>

      {/* KPIs rápidos del rango */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Card>
          <CardContent className="p-3 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <AlertCircle className="h-4 w-4 text-amber-700 dark:text-amber-300" />
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Partes abiertos (sin cerrar)</p>
              <p className="text-lg font-bold">{partesPendientes.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
              <UtensilsCrossed className="h-4 w-4 text-orange-700 dark:text-orange-300" />
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Almuerzos sin enviar</p>
              <p className="text-lg font-bold">{partesSinAlmuerzo.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
              <Lock className="h-4 w-4 text-emerald-700 dark:text-emerald-300" />
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Partes cerrados</p>
              <p className="text-lg font-bold">{data.partes.filter(p => p.estado === 'cerrado').length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Users className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">HH generadas total</p>
              <p className="text-lg font-bold">{data.days.reduce((a, d) => a + (d.total_hh || 0), 0)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendario horizontal por día */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Resumen por día · click en un día para ver detalle</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : data.days.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Sin partes en el rango seleccionado.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
              {data.days.map(d => {
                const isSelected = d.fecha_iso === diaSeleccionado;
                const esDiaViejoAbierto = d.abiertos > 0 && (Date.now() - new Date(d.fecha_iso).getTime()) > TERCER_DIA_MS;
                return (
                  <button
                    key={d.fecha_iso}
                    onClick={() => setDiaSeleccionado(d.fecha_iso)}
                    className={`text-left p-2.5 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/30'
                        : esDiaViejoAbierto
                        ? 'border-red-300 bg-red-50/50 hover:bg-red-50 dark:bg-red-900/10'
                        : 'border-border hover:bg-muted/40'
                    }`}
                    data-testid={`day-card-${d.fecha_iso}`}
                  >
                    <p className="text-[10px] text-muted-foreground uppercase">{formatDateShort(d.fecha_iso)}</p>
                    <p className="text-xs font-semibold mb-1">{d.total_partes} partes</p>
                    <div className="flex flex-wrap gap-1 text-[10px]">
                      {d.cerrados > 0 && (
                        <span className="px-1 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">
                          ✓ {d.cerrados}
                        </span>
                      )}
                      {d.abiertos > 0 && (
                        <span className={`px-1 rounded ${esDiaViejoAbierto ? 'bg-red-200 text-red-900' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200'}`}>
                          📝 {d.abiertos}
                        </span>
                      )}
                      {d.almuerzo_pendiente > 0 && (
                        <span className="px-1 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                          🍽️ {d.almuerzo_pendiente}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detalle del día seleccionado */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            <span>Partes del {formatDateShort(diaSeleccionado)}</span>
            {dayStats && (
              <span className="text-xs font-normal text-muted-foreground">
                {dayStats.total_personal} personas · {dayStats.total_almuerzos} almuerzos · {dayStats.total_hh} HH
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="partes-del-dia-table">
              <thead>
                <tr className="bg-muted/40 text-left">
                  <th className="px-3 py-2 font-medium">OT</th>
                  <th className="px-3 py-2 font-medium">Descripción</th>
                  <th className="px-3 py-2 font-medium">Supervisor</th>
                  <th className="px-3 py-2 font-medium text-center">VB</th>
                  <th className="px-3 py-2 font-medium text-center">Personal</th>
                  <th className="px-3 py-2 font-medium">🍽️ Almuerzo</th>
                  <th className="px-3 py-2 font-medium">🕐 OT y Horario</th>
                  <th className="px-3 py-2 font-medium text-center">Acción</th>
                </tr>
              </thead>
              <tbody>
                {partesDelDia.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">
                      Sin partes para este día.
                    </td>
                  </tr>
                ) : partesDelDia.map(p => (
                  <tr key={p.parte_id} className="border-t hover:bg-muted/20" data-testid={`parte-row-${p.parte_id}`}>
                    <td className="px-3 py-2 font-mono font-semibold">{p.ot_principal}</td>
                    <td className="px-3 py-2 max-w-[240px] truncate" title={p.ot_descripcion}>
                      {p.ot_descripcion}
                      {p.cliente_nombre && <span className="text-muted-foreground ml-1">({p.cliente_nombre})</span>}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{p.supervisor_nombre || '—'}</td>
                    <td className="px-3 py-2 text-center" data-testid={`vb-cell-${p.parte_id}`}>
                      {p.estado === 'cerrado'
                        && (p.supervisor_nombre || '').trim()
                        && !(p.vb_supervisor && p.vb_supervisor.dado) ? (
                        <button
                          onClick={() => openDarVB(p)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-200"
                          title={`Dar VB del supervisor ${p.supervisor_nombre} (kiosko)`}
                          data-testid={`btn-dar-vb-${p.parte_id}`}
                        >
                          <ShieldCheck className="h-3 w-3" /> Dar VB
                        </button>
                      ) : (
                        <VBStatusChip parte={p} />
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="font-semibold">{p.total_presentes}</span>
                      {p.total_ausentes > 0 && <span className="text-muted-foreground"> / {p.total_personal}</span>}
                    </td>
                    <td className="px-3 py-2"><LunchChip parte={p} /></td>
                    <td className="px-3 py-2"><AsignacionChip parte={p} /></td>
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => irAlParte(p.parte_id)}
                        className="text-[11px] text-primary hover:underline font-medium"
                        data-testid={`btn-ir-parte-${p.parte_id}`}
                      >
                        Abrir →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Alertas: partes con almuerzos sin enviar */}
      {partesSinAlmuerzo.length > 0 && (
        <Card className="border-amber-300 dark:border-amber-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <AlertCircle className="h-4 w-4" /> Partes con almuerzos sin enviar ({partesSinAlmuerzo.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {partesSinAlmuerzo.slice(0, 20).map(p => (
                <div key={p.parte_id} className="flex items-center justify-between text-xs bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1.5">
                  <div>
                    <span className="font-semibold">{p.fecha}</span>
                    <span className="ml-2">OT {p.ot_principal}</span>
                    <span className="ml-2 text-muted-foreground">{p.ot_descripcion?.slice(0, 40)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{p.total_almuerzos} almuerzos</span>
                    <button onClick={() => irAlParte(p.parte_id)} className="text-primary hover:underline" data-testid={`btn-ir-alm-${p.parte_id}`}>
                      Abrir →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      {/* Modal: Dar VB en modo kiosko (cualquier sesión, PIN del supervisor asignado) */}
      <Dialog open={!!vbParte} onOpenChange={(o) => !o && setVbParte(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-dar-vb-kiosko">
          {vbParte && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-emerald-600" />
                  Visto Bueno · Modo Kiosko
                </DialogTitle>
                <DialogDescription>
                  Parte del <strong>{vbParte.fecha}</strong> · OT {vbParte.ot_principal || '-'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded p-3 text-sm">
                  <p className="text-xs text-emerald-700 dark:text-emerald-300 mb-1">Firmando como supervisor:</p>
                  <p className="font-semibold text-emerald-900 dark:text-emerald-100" data-testid="vb-supervisor-nombre">
                    {vbParte.supervisor_nombre}
                  </p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-900 border rounded p-3 text-xs space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">Personal presente:</span><span className="font-medium">{vbParte.total_presentes}/{vbParte.total_personal}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">HH generadas:</span><span className="font-medium">{vbParte.hh_generados || 0}</span></div>
                  {vbParte.cliente_nombre && (
                    <div className="flex justify-between"><span className="text-muted-foreground">Cliente:</span><span>{vbParte.cliente_nombre}</span></div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    PIN VB de <span className="text-emerald-700 dark:text-emerald-300">{vbParte.supervisor_nombre}</span>
                  </label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={8}
                    value={vbPin}
                    onChange={(e) => setVbPin(e.target.value.replace(/\D/g, ''))}
                    placeholder="••••"
                    autoComplete="off"
                    autoFocus
                    data-testid="input-vb-kiosko-pin"
                    className="text-2xl tracking-widest text-center font-mono"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Pasale el celular o esta pantalla al supervisor para que ingrese su PIN personal.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Comentario (opcional)</label>
                  <Textarea
                    rows={2}
                    value={vbComentario}
                    onChange={(e) => setVbComentario(e.target.value)}
                    placeholder="Observaciones sobre el parte…"
                    maxLength={500}
                    data-testid="input-vb-kiosko-comentario"
                  />
                </div>
              </div>
              <DialogFooter className="gap-2 flex-col sm:flex-row">
                <Button variant="outline" onClick={() => setVbParte(null)} disabled={vbSubmitting} data-testid="btn-vb-cancelar">
                  Cancelar
                </Button>
                <Button
                  onClick={submitDarVB}
                  disabled={vbSubmitting || !vbPin || vbPin.length < 4}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-vb-confirmar"
                >
                  {vbSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                  Firmar VB
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Iter 236.q (Carlos): Dialog confirmar V°B° masivo del mes */}
      <Dialog open={vbMasivoOpen} onOpenChange={(o) => !o && !vbMasivoLoading && setVbMasivoOpen(false)}>
        <DialogContent className="max-w-md" data-testid="dialog-vb-masivo-mes">
          <DialogHeader>
            <DialogTitle className="text-amber-700">V°B° Masivo del Mes</DialogTitle>
            <DialogDescription>
              Esta acción aplicará <b>V°B° a TODOS los partes diarios cerrados del mes en curso</b> que aún no lo tengan.
              Es una acción extraordinaria y queda registrada con tu nombre y la marca <code>vb_masivo_extraordinario</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
            ⚠️ Confirma solo si estás seguro. No se puede deshacer automáticamente — habría que revertir parte por parte.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVbMasivoOpen(false)} disabled={vbMasivoLoading} data-testid="btn-vb-masivo-cancel">
              Cancelar
            </Button>
            <Button onClick={aplicarVbMasivo} disabled={vbMasivoLoading} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-vb-masivo-confirm">
              {vbMasivoLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Stamp className="h-4 w-4 mr-2" />}
              Aplicar V°B° al mes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
